/* eslint-disable react/jsx-no-bind */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertsContext } from '../shared/alerts/Alerts.context';
import SpinningSpark from '../shared/components/SpinningSpark';
import adminFormStyles from '../shared/styles/adminForm.styles';
import TemplatesApi from './Templates.api';
import TemplateForm from './forms/TemplateForm';
import {
  parseTemplateFiltersFromSearchParams,
  persistTemplatesListSearch,
  readTemplatesListSearch,
  resolveTemplatesListSearchAfterSave,
  resolveTemplatesListSearchOnCancel,
  templatesListSearchToURLSearchParams,
} from './templateFilters.utils';

const styles = {
  page: adminFormStyles.page,
  content: adminFormStyles.content,
  centered: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    minHeight: 320,
  },
  loadError: {
    ...adminFormStyles.section,
    marginX: 'auto',
    maxWidth: 560,
    p: 4,
    textAlign: 'center',
  },
  templatesButton: {
    borderRadius: '8px',
    textTransform: 'none',
  },
};

function normalizeTemplateForForm(template) {
  return {
    ...template,
    topic: template.topic?.id ?? template.topic,
    subtopic: template.subtopic?.id ?? template.subtopic,
  };
}

function getTemplateFilterContext(template) {
  if (!template) {
    return null;
  }

  return {
    category: template.category,
    topic: template.topic,
    subtopic: template.subtopic,
  };
}

export default function TemplateFormPage() {
  const { template_id: templateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { showAlert } = useContext(AlertsContext);
  const [topics, setTopics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const isEditing = Boolean(templateId);
  const [initialTemplate, setInitialTemplate] = useState(null);

  const listSearch = readTemplatesListSearch(location);

  const createTemplateDefaults = useMemo(() => {
    if (isEditing) {
      return null;
    }

    const filters = parseTemplateFiltersFromSearchParams(
      templatesListSearchToURLSearchParams(listSearch)
    );

    const defaults = {
      ...(filters.postType ? { category: filters.postType } : {}),
      ...(filters.topic ? { topic: filters.topic } : {}),
      ...(filters.subtopic ? { subtopic: filters.subtopic } : {}),
    };

    return Object.keys(defaults).length > 0 ? defaults : null;
  }, [isEditing, listSearch]);

  async function loadTopics() {
    try {
      const fetchedTopics = await TemplatesApi.getTopics();
      setTopics(fetchedTopics);
      return fetchedTopics;
    } catch (error) {
      console.error('Failed to load topics', error);
      showAlert('Failed to load topics. Please try again.', 'error');
      throw error;
    }
  }

  useEffect(() => {
    persistTemplatesListSearch(listSearch);
  }, [listSearch]);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      setIsLoading(true);
      setLoadFailed(false);
      setInitialTemplate(null);

      try {
        await loadTopics();
        if (isEditing) {
          const fetched = await TemplatesApi.getTemplate(templateId);
          if (!cancelled) {
            setInitialTemplate(normalizeTemplateForForm(fetched));
          }
        }
      } catch {
        if (!cancelled) {
          setLoadFailed(true);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, [templateId]);

  function navigateToTemplatesList(nextListSearch) {
    persistTemplatesListSearch(nextListSearch);
    navigate({
      pathname: '/templates',
      search: nextListSearch,
    });
  }

  function handleBack() {
    navigateToTemplatesList(
      resolveTemplatesListSearchOnCancel(
        listSearch,
        getTemplateFilterContext(initialTemplate)
      )
    );
  }

  function handleSaved(savedTemplate) {
    navigateToTemplatesList(resolveTemplatesListSearchAfterSave(savedTemplate));
  }

  async function handleTopicsChanged() {
    await loadTopics();
  }

  function renderContent() {
    if (isLoading) {
      return (
        <Box sx={styles.centered}>
          <SpinningSpark height="50" />
        </Box>
      );
    }

    if (loadFailed || (isEditing && !initialTemplate)) {
      return (
        <Paper variant="outlined" sx={styles.loadError}>
          <Stack spacing={2} alignItems="center">
            <Typography component="h1" variant="h5">
              Template not found
            </Typography>
            <Button
              type="button"
              variant="contained"
              onClick={handleBack}
              sx={styles.templatesButton}
            >
              Back to templates
            </Button>
          </Stack>
        </Paper>
      );
    }

    return (
      <TemplateForm
        key={isEditing ? initialTemplate?.id ?? templateId : 'new'}
        initialTemplate={isEditing ? initialTemplate : createTemplateDefaults}
        topics={topics}
        onBack={handleBack}
        onSaved={handleSaved}
        onTopicsChanged={handleTopicsChanged}
      />
    );
  }

  return (
    <Box sx={styles.page}>
      <Box sx={styles.content}>{renderContent()}</Box>
    </Box>
  );
}
